import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BuyContractService } from '../buy-contract.service';
import { AutoBuyConfig, AutoBuySupplierScope } from '../buy-contract.type';
import { SupplierService } from '../../../supplier/supplier.service';
import { SupplierProfile } from '../../../supplier/supplier.type';
import { StockLeasesDialog } from '../stock-leases-dialog/stock-leases-dialog';

/**
 * Admin control for the singleton continuous auto-buy controller. Edits apply
 * to future leases without a restart; turning it off releases live leases and
 * all locked allocations.
 */
@Component({
  selector: 'app-auto-buy-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
  ],
  templateUrl: './auto-buy-dialog.html',
  styleUrl: './auto-buy-dialog.scss',
})
export class AutoBuyDialog implements OnInit {
  private fb = inject(FormBuilder);
  private service = inject(BuyContractService);
  private supplierService = inject(SupplierService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<AutoBuyDialog>);
  private snackBar = inject(MatSnackBar);

  loading = true;
  saving = false;
  config: AutoBuyConfig | null = null;
  suppliers: SupplierProfile[] = [];

  form = this.fb.nonNullable.group({
    is_active: [false],
    desired_concurrent_stocks: [1, [Validators.required, Validators.min(1), Validators.max(100)]],
    supplier_scope: ['all' as AutoBuySupplierScope],
    supplier_ids: [[] as string[]],
  });

  ngOnInit(): void {
    this.supplierService.listSuppliers({ pageSize: 200 }).subscribe({
      next: res => this.suppliers = res.suppliers,
      error: () => { },
    });
    this.service.getAutoBuyConfig().subscribe({
      next: cfg => {
        this.applyConfig(cfg);
        this.loading = false;
      },
      error: err => {
        this.snackBar.open(err.error?.error || 'Error loading auto-buy config', 'Close', { duration: 5000 });
        this.dialogRef.close();
      },
    });
  }

  private applyConfig(cfg: AutoBuyConfig): void {
    this.config = cfg;
    this.form.reset({
      is_active: cfg.is_active,
      desired_concurrent_stocks: cfg.desired_concurrent_stocks || 1,
      supplier_scope: cfg.supplier_scope ?? 'all',
      supplier_ids: cfg.supplier_ids ?? [],
    });
  }

  get isSelectedScope(): boolean {
    return this.form.controls.supplier_scope.value === 'selected';
  }

  get canSave(): boolean {
    if (this.saving || this.form.invalid || !this.form.dirty) return false;
    return !this.isSelectedScope || this.form.controls.supplier_ids.value.length > 0;
  }

  supplierName(id: string): string {
    return this.suppliers.find(s => s.user_id === id)?.name ?? id;
  }

  save(): void {
    if (!this.canSave) return;
    const v = this.form.getRawValue();
    this.saving = true;
    this.service.updateAutoBuyConfig({
      is_active: v.is_active,
      desired_concurrent_stocks: v.desired_concurrent_stocks,
      supplier_scope: v.supplier_scope,
      // [] explicitly clears the selection when going back to "all".
      supplier_ids: v.supplier_scope === 'selected' ? v.supplier_ids : [],
    }).subscribe({
      next: cfg => {
        this.applyConfig(cfg);
        this.saving = false;
        this.snackBar.open('Auto-buy updated', 'Close', { duration: 4000 });
      },
      error: err => {
        this.saving = false;
        this.snackBar.open(err.error?.error || 'Error updating auto-buy', 'Close', { duration: 5000 });
      },
    });
  }

  viewLeases(): void {
    if (!this.config?.buy_contract_id) return;
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(StockLeasesDialog, {
      data: { config: this.config },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
